import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { money } from '../../utils/region';
import { useReadOnlySeat } from '../../utils/readOnlySeat';

// ── The statutory registers ─────────────────────────────────────────────────────────────────────
// Eight obligations that had no screen, and in most cases no table. What they have in common is that
// every one is a DEADLINE measured from a date nobody was recording — so the register is the feature,
// and the clock is the point.
//
// Each section shows its own rule in the operator's own words, because these are the parts of the Act
// an accountant meets once every few years and has to get right the first time.
//
// Operator labels run through t('pa_reg_…'). Statutory Thai names (รายงานของกรรมการ, ทะเบียนสำนักงานบัญชี,
// ใบกำกับภาษีขาย, รายงานการชำระบัญชี) carry the same Thai value in every locale: that is the name the
// server prints on the document face. Server-returned text (d.rule, c.deadlineNote, x.th, r.why,
// requirement.note, error messages) is DATA and is never wrapped in t().
//
// ⛔ A read-only seat (a client's own books login, the auditor — utils/readOnlySeat.ts) reads every
// register and is drawn no write control: the server refuses each write that seat sends and every
// refusal pages the admins. Entry rows, row actions (Notified / Extend / Delivered / Answered /
// Registered), the directors' report Save and the strike button are not drawn; the directors' report
// and the cost formula show as text. The inspection pack's preview and download, the market-value
// what-if and the folds are reads and stay. The FIRM's own registration is not the client's at all —
// both its routes are denied to a books login — so that section is not mounted for the seat.

const dmy = (iso: string) => { const p = String(iso || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso; };
const baht = (s: any) => money(Number(s || 0) / 100);
const toSatang = (v: string) => Math.round((Number(v) || 0) * 100);
const today = () => new Date().toISOString().slice(0, 10);

const btn: React.CSSProperties = {
  padding: '6px 11px', fontSize: 12, fontWeight: 700, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: 'pointer',
};
const inp: React.CSSProperties = {
  padding: '6px 9px', fontSize: 12.5, borderRadius: 7, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box',
};
const row: React.CSSProperties = { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 9 };
const amber: React.CSSProperties = {
  fontSize: 11.5, color: '#92400E', background: '#FEF6E7', border: '1px solid #F5D9A8',
  padding: '7px 10px', borderRadius: 8, lineHeight: 1.6,
};
const red: React.CSSProperties = {
  fontSize: 11.5, color: '#B42318', background: '#FCEBEB', border: '1px solid #F3B9B9',
  padding: '7px 10px', borderRadius: 8, lineHeight: 1.6,
};
const rule: React.CSSProperties = { fontSize: 11.5, color: 'var(--text-tertiary)', lineHeight: 1.6, marginBottom: 9 };
const th: React.CSSProperties = { textAlign: 'left', fontSize: 10.5, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)', padding: '5px 8px', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { fontSize: 12, color: 'var(--text-primary)', padding: '5px 8px', borderTop: '1px solid var(--border)', verticalAlign: 'top' };
const num: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

function Section({ title, subtitle, badge, children }: { title: string; subtitle: string; badge?: React.ReactNode; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)', overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '10px 13px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <i className={`ti ti-chevron-${open ? 'down' : 'right'}`} style={{ fontSize: 15, color: 'var(--text-tertiary)' }} aria-hidden />
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</span>
          <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>{subtitle}</span>
        </span>
        {badge}
      </button>
      {open && <div style={{ padding: '2px 13px 13px' }}>{children}</div>}
    </div>
  );
}

const Pill = ({ n, tone }: { n: number; tone: 'red' | 'amber' }) => n > 0 ? (
  <span style={{ fontSize: 11, fontWeight: 800, padding: '1px 8px', borderRadius: 999, whiteSpace: 'nowrap',
    color: tone === 'red' ? '#B42318' : '#92400E', background: tone === 'red' ? '#FCEBEB' : '#FEF6E7',
    border: `1px solid ${tone === 'red' ? '#F3B9B9' : '#F5D9A8'}` }}>{n}</span>
) : null;

function Err({ e }: { e: string }) { return e ? <div style={red}>{e}</div> : null; }

/** ss.15-16 — lost or damaged, notified within 15 days of DISCOVERY. */
function Incidents({ clientId, periodId, readOnly }: { clientId: string; periodId: string; readOnly: boolean }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [f, setF] = useState({ kind: 'lost', discoveredOn: '', occurredOn: '', scope: '', cause: '', description: '' });
  const [err, setErr] = useState('');
  const load = useCallback(async () => { try { setD(await api.arIncidents(clientId)); } catch (e: any) { setErr(e.message); } }, [clientId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={rule}>{d?.rule}</div>
      {!readOnly && <div style={row}>
        <select value={f.kind} onChange={e => setF({ ...f, kind: e.target.value })} style={inp}>
          <option value="lost">{t('pa_reg_incident_kind_lost')}</option><option value="damaged">{t('pa_reg_incident_kind_damaged')}</option>
        </select>
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_reg_incident_discovered')}<br />
          <input type="date" value={f.discoveredOn} onChange={e => setF({ ...f, discoveredOn: e.target.value })} style={{ ...inp, width: 140 }} /></label>
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_reg_incident_happened')}<br />
          <input type="date" value={f.occurredOn} onChange={e => setF({ ...f, occurredOn: e.target.value })} style={{ ...inp, width: 140 }} /></label>
        <input placeholder={t('pa_reg_incident_scope_ph')} value={f.scope} onChange={e => setF({ ...f, scope: e.target.value })} style={{ ...inp, width: 200 }} />
        <input placeholder={t('pa_reg_incident_cause_ph')} value={f.cause} onChange={e => setF({ ...f, cause: e.target.value })} style={{ ...inp, width: 250 }} />
        <button style={btn} onClick={async () => {
          setErr('');
          try { const r: any = await api.arAddIncident(clientId, { ...f, periodId });
                if (r.presumedIntentional) window.alert(t('pa_reg_incident_presumed_alert'));
                setF({ ...f, scope: '', cause: '', description: '' }); await load(); }
          catch (e: any) { setErr(e.message); }
        }}><i className="ti ti-plus" aria-hidden /> {t('pa_reg_record')}</button>
      </div>}
      {(d?.rows || []).length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>{t('pa_reg_incident_th_discovered')}</th><th style={th}>{t('pa_reg_incident_th_what')}</th><th style={th}>{t('pa_reg_incident_th_scope')}</th><th style={th}>{t('pa_reg_incident_th_cause')}</th><th style={th}>{t('pa_reg_incident_th_notify_by')}</th><th style={th}>{t('pa_reg_incident_th_notified')}</th>{!readOnly && <th style={th} />}</tr></thead>
          <tbody>
            {d.rows.map((r: any) => (
              <tr key={r.id}>
                <td style={td}>{dmy(r.discoveredOn)}</td>
                <td style={td}>{r.kind === 'lost' ? t('pa_reg_incident_kind_lost') : t('pa_reg_incident_kind_damaged')}{r.recovered ? ` · ${t('pa_reg_incident_recovered')}` : ''}</td>
                <td style={td}>{r.scope}</td>
                <td style={{ ...td, color: r.presumedIntentional ? '#B42318' : 'var(--text-secondary)' }}>
                  {r.cause || t('pa_reg_incident_cause_blank')}</td>
                <td style={{ ...td, color: r.overdue ? '#B42318' : 'var(--text-secondary)' }}>
                  {dmy(r.effectiveDueBy)}{r.overdue ? ` · ${t('pa_reg_days_late', { n: r.daysLate })}` : ''}</td>
                <td style={td}>{r.notifiedOn ? `${dmy(r.notifiedOn)}${r.notifyRef ? ` · ${r.notifyRef}` : ''}` : '—'}</td>
                {!readOnly && <td style={td}>{!r.notifiedOn && (
                  <button style={{ ...btn, padding: '3px 7px', fontSize: 11 }} onClick={async () => {
                    const on = window.prompt(t('pa_reg_incident_notified_prompt'), today()); if (!on) return;
                    const ref = window.prompt(t('pa_reg_incident_epermit_prompt')) || '';
                    setErr('');
                    try { await api.arNotifyIncident(clientId, r.id, { notifiedOn: on, notifyRef: ref }); await load(); } catch (e: any) { setErr(e.message); }
                  }}>{t('pa_reg_incident_notified_btn')}</button>
                )}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pa_reg_empty')}</div>}
      {d?.unexplained ? <div style={{ ...amber, marginTop: 9 }}>
        {t('pa_reg_incident_unexplained', { n: d.unexplained })}
      </div> : null}
      <Err e={err} />
    </div>
  );
}

/** s.17 — ceasing business: 90 days to deliver, extendable to 180. */
function Cessation({ clientId, readOnly }: { clientId: string; readOnly: boolean }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [f, setF] = useState({ ceasedOn: '', reason: '' });
  const [err, setErr] = useState('');
  const load = useCallback(async () => { try { setD(await api.arCessation(clientId)); } catch (e: any) { setErr(e.message); } }, [clientId]);
  useEffect(() => { load(); }, [load]);
  const c = d?.cessation;
  return (
    <div>
      <div style={rule}>{d?.rule}</div>
      {!c ? (readOnly ? <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pa_reg_empty')}</div> : (
        <div style={row}>
          <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_reg_cess_ceased')}<br />
            <input type="date" value={f.ceasedOn} onChange={e => setF({ ...f, ceasedOn: e.target.value })} style={{ ...inp, width: 140 }} /></label>
          <input placeholder={t('pa_reg_cess_reason_ph')} value={f.reason} onChange={e => setF({ ...f, reason: e.target.value })} style={{ ...inp, width: 220 }} />
          <button style={btn} onClick={async () => {
            setErr('');
            try { await api.arAddCessation(clientId, f); await load(); } catch (e: any) { setErr(e.message); }
          }}><i className="ti ti-plus" aria-hidden /> {t('pa_reg_cess_record')}</button>
        </div>
      )) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          <div style={{ fontSize: 12.5, color: 'var(--text-primary)', lineHeight: 1.8 }}>
            {t('pa_reg_cess_ceased_on', { date: dmy(c.ceasedOn) })}{c.reason ? ` · ${c.reason}` : ''}<br />
            {t('pa_reg_cess_deliver_by')} <b>{dmy(c.deliverDueBy)}</b>{c.effectiveDueBy !== c.deliverDueBy ? ` · ${t('pa_reg_cess_extended_to', { date: dmy(c.effectiveDueBy) })}` : ''}
            {' '}<span style={{ color: 'var(--text-tertiary)' }}>{t('pa_reg_cess_max_extension', { date: dmy(c.maxExtensionTo) })}</span><br />
            {c.deliveredOn
              ? <span style={{ color: '#166534' }}>{t('pa_reg_cess_delivered', { date: dmy(c.deliveredOn) })}{c.receiptRef ? ` · ${c.receiptRef}` : ''}</span>
              : <span style={{ color: c.overdue ? '#B42318' : 'var(--text-secondary)' }}>{t('pa_reg_cess_not_delivered')}{c.overdue ? ` · ${t('pa_reg_cess_overdue_days', { n: c.daysLate })}` : ''}</span>}
          </div>
          {!c.deliveredOn && !readOnly && (
            <div style={row}>
              <button style={btn} onClick={async () => {
                const to = window.prompt(t('pa_reg_cess_extend_prompt', { date: c.maxExtensionTo })); if (!to) return;
                const ref = window.prompt(t('pa_reg_cess_extend_ref_prompt')) || '';
                setErr('');
                try { await api.arExtendCessation(clientId, c.id, { extensionTo: to, extensionRef: ref }); await load(); } catch (e: any) { setErr(e.message); }
              }}>{t('pa_reg_cess_extend_btn')}</button>
              <button style={btn} onClick={async () => {
                const on = window.prompt(t('pa_reg_cess_delivered_prompt'), today()); if (!on) return;
                const ref = window.prompt(t('pa_reg_cess_receipt_prompt')) || '';
                setErr('');
                try { await api.arDeliverCessation(clientId, c.id, { deliveredOn: on, receiptRef: ref }); await load(); } catch (e: any) { setErr(e.message); }
              }}>{t('pa_reg_cess_delivered_btn')}</button>
            </div>
          )}
        </div>
      )}
      <Err e={err} />
    </div>
  );
}

/** ss.22/24/25 — an inspector's demand, and the bundle that answers it. */
function Inspections({ clientId, periodId, readOnly }: { clientId: string; periodId: string; readOnly: boolean }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [want, setWant] = useState<string[]>(['books', 'documents', 'code_manual']);
  const [manifest, setManifest] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [f, setF] = useState({ requestedOn: '', authority: 'DBD', inspectorName: '', dueOn: '' });
  const load = useCallback(async () => { try { setD(await api.arInspections(clientId)); } catch (e: any) { setErr(e.message); } }, [clientId]);
  useEffect(() => { load(); }, [load]);
  const toggle = (k: string) => setWant(w => w.includes(k) ? w.filter(x => x !== k) : [...w, k]);
  return (
    <div>
      <div style={rule}>{d?.rule}</div>
      {!readOnly && <div style={row}>
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_reg_insp_demanded')}<br />
          <input type="date" value={f.requestedOn} onChange={e => setF({ ...f, requestedOn: e.target.value })} style={{ ...inp, width: 140 }} /></label>
        <input placeholder={t('pa_reg_insp_authority_ph')} value={f.authority} onChange={e => setF({ ...f, authority: e.target.value })} style={{ ...inp, width: 90 }} />
        <input placeholder={t('pa_reg_insp_inspector_ph')} value={f.inspectorName} onChange={e => setF({ ...f, inspectorName: e.target.value })} style={{ ...inp, width: 150 }} />
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_reg_insp_due')}<br />
          <input type="date" value={f.dueOn} onChange={e => setF({ ...f, dueOn: e.target.value })} style={{ ...inp, width: 140 }} /></label>
        <button style={btn} onClick={async () => {
          setErr('');
          try { await api.arAddInspection(clientId, { ...f, requested: want }); await load(); } catch (e: any) { setErr(e.message); }
        }}><i className="ti ti-plus" aria-hidden /> {t('pa_reg_insp_record')}</button>
      </div>}
      <div style={{ ...row, alignItems: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{t('pa_reg_insp_asked_label')}</span>
        {(d?.requestable || []).map((x: any) => (
          <label key={x.key} style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', gap: 4, alignItems: 'center' }}>
            <input type="checkbox" checked={want.includes(x.key)} onChange={() => toggle(x.key)} /> {x.th}
          </label>
        ))}
        <button disabled={busy === 'pv'} style={btn} onClick={async () => {
          setBusy('pv'); setErr('');
          try { const r: any = await api.arPackPreview(clientId, periodId, want); setManifest(r.manifest); }
          catch (e: any) { setErr(e.message); } finally { setBusy(''); }
        }}><i className={busy === 'pv' ? 'ti ti-loader' : 'ti ti-eye'} aria-hidden /> {t('pa_reg_insp_preview')}</button>
        <button disabled={busy === 'dl'} style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none' }} onClick={async () => {
          setBusy('dl'); setErr('');
          try { const url = await api.arPackUrl(clientId, periodId, want); window.open(url, '_blank'); }
          catch (e: any) { setErr(e.message); } finally { setBusy(''); }
        }}><i className={busy === 'dl' ? 'ti ti-loader' : 'ti ti-package'} aria-hidden /> {t('pa_reg_insp_build')}</button>
      </div>
      {manifest && <pre style={{ fontSize: 11, lineHeight: 1.6, background: 'var(--bg-app)', border: '1px solid var(--border)', borderRadius: 8, padding: 11, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>{manifest}</pre>}
      {(d?.inspections || []).length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 9 }}>
          <thead><tr><th style={th}>{t('pa_reg_insp_th_demanded')}</th><th style={th}>{t('pa_reg_insp_th_by')}</th><th style={th}>{t('pa_reg_insp_th_asked')}</th><th style={th}>{t('pa_reg_insp_th_due')}</th><th style={th}>{t('pa_reg_insp_th_answered')}</th>{!readOnly && <th style={th} />}</tr></thead>
          <tbody>
            {d.inspections.map((r: any) => (
              <tr key={r.id}>
                <td style={td}>{dmy(r.requestedOn)}</td>
                <td style={td}>{r.authority}{r.inspectorName ? ` · ${r.inspectorName}` : ''}</td>
                <td style={{ ...td, fontSize: 11 }}>{r.requestedTh.join(', ')}</td>
                <td style={{ ...td, color: r.overdue ? '#B42318' : 'var(--text-secondary)' }}>{r.dueOn ? dmy(r.dueOn) : '—'}</td>
                <td style={td}>{r.respondedOn ? dmy(r.respondedOn) : '—'}</td>
                {!readOnly && <td style={td}>{!r.respondedOn && (
                  <button style={{ ...btn, padding: '3px 7px', fontSize: 11 }} onClick={async () => {
                    const on = window.prompt(t('pa_reg_insp_answered_prompt'), today()); if (!on) return;
                    setErr('');
                    try { await api.arRespondInspection(clientId, r.id, { respondedOn: on }); await load(); } catch (e: any) { setErr(e.message); }
                  }}>{t('pa_reg_insp_answered_btn')}</button>
                )}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      <Err e={err} />
    </div>
  );
}

/** Capital / articles / directors within 14 days, and the liquidation's three-month cycle. */
function Changes({ clientId, readOnly }: { clientId: string; readOnly: boolean }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [f, setF] = useState({ changeKind: 'directors', resolvedOn: '', description: '', oldValue: '', newValue: '' });
  const [err, setErr] = useState('');
  const load = useCallback(async () => { try { setD(await api.arChanges(clientId)); } catch (e: any) { setErr(e.message); } }, [clientId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={rule}>{d?.rule}</div>
      {!readOnly && <div style={row}>
        <select value={f.changeKind} onChange={e => setF({ ...f, changeKind: e.target.value })} style={{ ...inp, minWidth: 200 }}>
          {(d?.kinds || []).map((k: any) => <option key={k.key} value={k.key}>{k.th} · {k.en}</option>)}
        </select>
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_reg_chg_resolved')}<br />
          <input type="date" value={f.resolvedOn} onChange={e => setF({ ...f, resolvedOn: e.target.value })} style={{ ...inp, width: 140 }} /></label>
        <input placeholder={t('pa_reg_chg_from_ph')} value={f.oldValue} onChange={e => setF({ ...f, oldValue: e.target.value })} style={{ ...inp, width: 150 }} />
        <input placeholder={t('pa_reg_chg_to_ph')} value={f.newValue} onChange={e => setF({ ...f, newValue: e.target.value })} style={{ ...inp, width: 150 }} />
        <button style={btn} onClick={async () => {
          setErr('');
          try { await api.arAddChange(clientId, f); setF({ ...f, description: '', oldValue: '', newValue: '' }); await load(); }
          catch (e: any) { setErr(e.message); }
        }}><i className="ti ti-plus" aria-hidden /> {t('pa_reg_record')}</button>
      </div>}
      {d?.nextLiquidationReport && (
        <div style={d.nextLiquidationReport.overdue ? red : amber}>
          {t('pa_reg_doc_liquidation_report')} — {t('pa_reg_chg_liquidation_due', { due: dmy(d.nextLiquidationReport.dueBy), since: dmy(d.nextLiquidationReport.since) })}
          {d.nextLiquidationReport.overdue ? ` · ${t('pa_reg_overdue')}` : ''}
        </div>
      )}
      {(d?.rows || []).length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 9 }}>
          <thead><tr><th style={th}>{t('pa_reg_chg_th_resolved')}</th><th style={th}>{t('pa_reg_chg_th_what')}</th><th style={th}>{t('pa_reg_chg_th_fromto')}</th><th style={th}>{t('pa_reg_chg_th_register_by')}</th><th style={th}>{t('pa_reg_chg_th_registered')}</th>{!readOnly && <th style={th} />}</tr></thead>
          <tbody>
            {d.rows.map((r: any) => (
              <tr key={r.id}>
                <td style={td}>{dmy(r.resolvedOn)}</td>
                <td style={td}>{r.label?.th}</td>
                <td style={{ ...td, fontSize: 11 }}>{r.oldValue || r.newValue ? `${r.oldValue || '—'} → ${r.newValue || '—'}` : (r.description || '—')}</td>
                <td style={{ ...td, color: r.overdue ? '#B42318' : 'var(--text-secondary)' }}>
                  {dmy(r.registerDueBy)}{r.overdue ? ` · ${t('pa_reg_days_late', { n: r.daysLate })}` : ''}</td>
                <td style={td}>{r.registeredOn ? `${dmy(r.registeredOn)}${r.receiptNo ? ` · ${r.receiptNo}` : ''}` : '—'}</td>
                {!readOnly && <td style={td}>{!r.registeredOn && (
                  <button style={{ ...btn, padding: '3px 7px', fontSize: 11 }} onClick={async () => {
                    const on = window.prompt(t('pa_reg_chg_registered_prompt'), today()); if (!on) return;
                    const no = window.prompt(t('pa_reg_chg_receipt_prompt')) || '';
                    setErr('');
                    try { await api.arRegisterChange(clientId, r.id, { registeredOn: on, receiptNo: no }); await load(); } catch (e: any) { setErr(e.message); }
                  }}>{t('pa_reg_chg_registered_btn')}</button>
                )}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pa_reg_empty')}</div>}
      <Err e={err} />
    </div>
  );
}

/** CCC s.1198 — the directors' report. No form is prescribed, so this is a text the firm drafts. */
function DirectorsReport({ clientId, periodId, readOnly }: { clientId: string; periodId: string; readOnly: boolean }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [f, setF] = useState({ bodyTh: '', bodyEn: '', agmDate: '', presentedOn: '' });
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const load = useCallback(async () => {
    try {
      const r: any = await api.arDirectorsReport(clientId, periodId);
      setD(r);
      if (r.report) setF({ bodyTh: r.report.body_th || '', bodyEn: r.report.body_en || '', agmDate: r.report.agm_date || '', presentedOn: r.report.presented_on || '' });
    } catch (e: any) { setErr(e.message); }
  }, [clientId, periodId]);
  useEffect(() => { load(); }, [load]);
  // The seat reads the report the firm drafted, as text: the fields below only exist to be saved.
  if (readOnly) {
    const body = (label: string, v: string) => (
      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{label}
        <div style={{ fontSize: 12.5, color: 'var(--text-primary)', lineHeight: 1.6, whiteSpace: 'pre-wrap', marginTop: 3 }}>{v || '—'}</div>
      </div>
    );
    return (
      <div>
        <div style={rule}>{d?.rule}</div>
        <div style={{ ...row, fontSize: 12.5, color: 'var(--text-primary)' }}>
          <span><span style={{ color: 'var(--text-secondary)' }}>{t('pa_reg_dr_meeting')}</span> {f.agmDate ? dmy(f.agmDate) : '—'}</span>
          <span><span style={{ color: 'var(--text-secondary)' }}>{t('pa_reg_dr_presented')}</span> {f.presentedOn ? dmy(f.presentedOn) : '—'}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 9 }}>
          {body(t('pa_reg_dr_body_th_label'), f.bodyTh)}
          {body(t('pa_reg_dr_body_en_label'), f.bodyEn)}
        </div>
        <Err e={err} />
      </div>
    );
  }
  return (
    <div>
      <div style={rule}>{d?.rule}</div>
      <div style={row}>
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_reg_dr_meeting')}<br />
          <input type="date" value={f.agmDate} onChange={e => setF({ ...f, agmDate: e.target.value })} style={{ ...inp, width: 140 }} /></label>
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_reg_dr_presented')}<br />
          <input type="date" value={f.presentedOn} onChange={e => setF({ ...f, presentedOn: e.target.value })} style={{ ...inp, width: 140 }} /></label>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 9 }}>
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_reg_dr_body_th_label')}
          <textarea value={f.bodyTh} onChange={e => setF({ ...f, bodyTh: e.target.value })} rows={7}
            style={{ ...inp, width: '100%', fontFamily: 'inherit', resize: 'vertical' }} /></label>
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_reg_dr_body_en_label')}
          <textarea value={f.bodyEn} onChange={e => setF({ ...f, bodyEn: e.target.value })} rows={7}
            style={{ ...inp, width: '100%', fontFamily: 'inherit', resize: 'vertical' }} /></label>
      </div>
      <div style={{ ...row, marginTop: 9 }}>
        <button style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none' }} onClick={async () => {
          setErr(''); setMsg('');
          try { await api.arSaveDirectorsReport(clientId, periodId, f); setMsg(t('pa_reg_saved')); await load(); } catch (e: any) { setErr(e.message); }
        }}><i className="ti ti-device-floppy" aria-hidden /> {t('pa_reg_save')}</button>
        {msg && <span style={{ fontSize: 12, color: '#166534' }}>{msg}</span>}
      </div>
      <Err e={err} />
    </div>
  );
}

/** The 1.8M VAT threshold. No due date is ever printed — see the service header. */
function VatThreshold({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  useEffect(() => { api.arVatThreshold(clientId).then(setD).catch((e: any) => setErr(e.message)); }, [clientId]);
  const c = d?.check;
  const pct = c ? Math.min(100, Math.round((c.revenueSatang / c.thresholdSatang) * 100)) : 0;
  return (
    <div>
      {c && (
        <>
          <div style={{ fontSize: 12.5, color: 'var(--text-primary)', lineHeight: 1.8, marginBottom: 8 }}>
            {t('pa_reg_vat_rev12', { from: dmy(c.from), to: dmy(c.asOf) })} : <b>{baht(c.revenueSatang)}</b> / {baht(c.thresholdSatang)} ({pct}%)
            <span style={{ color: 'var(--text-tertiary)' }}> · {t('pa_reg_vat_source', { source: c.source === 'ledger' ? t('pa_reg_vat_source_ledger') : t('pa_reg_doc_sales_tax_invoice') })}</span>
          </div>
          <div style={{ height: 8, borderRadius: 999, background: 'var(--bg-app)', border: '1px solid var(--border)', overflow: 'hidden', marginBottom: 9 }}>
            <div style={{ width: `${pct}%`, height: '100%', background: c.exceeded ? '#B42318' : (pct > 80 ? '#B45309' : 'var(--accent)') }} />
          </div>
          {c.exceeded && <div style={red}><b>{t('pa_reg_vat_exceeded_head')}</b> {t('pa_reg_vat_exceeded_body')}</div>}
          <div style={{ ...amber, marginTop: 8 }}>{c.deadlineNote}</div>
        </>
      )}
      <Err e={err} />
    </div>
  );
}

/** s.65 bis (6)-(7) — closing stock at the lower of cost or market. */
function StockValuation({ clientId, periodId, closed, readOnly, onChanged }: { clientId: string; periodId: string; closed: boolean; readOnly: boolean; onChanged: () => void }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [market, setMarket] = useState<Record<string, string>>({});
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const asSatang = () => Object.fromEntries(Object.entries(market).filter(([, v]) => String(v).trim() !== '').map(([k, v]) => [k, toSatang(v)]));
  const load = useCallback(async () => {
    try { setD(await api.arStockValuation(clientId, periodId, asSatang())); } catch (e: any) { setErr(e.message); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, periodId, market]);
  useEffect(() => { load(); }, [load]);
  const pv = d?.preview;
  return (
    <div>
      <div style={rule}>{d?.rule}</div>
      <div style={row}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{t('pa_reg_stock_cost_formula')}</span>
        {readOnly
          ? (d && <span style={{ fontSize: 12.5, color: 'var(--text-primary)' }}>{(() => { const m = (d.methods || []).find((x: any) => x.key === d.method); return m ? `${m.th} · ${m.en}` : t('pa_reg_stock_method_none'); })()}</span>)
          : (
            <select value={d?.method || ''} onChange={async e => {
              setErr('');
              try { await api.arSetStockMethod(clientId, e.target.value); await load(); } catch (ex: any) { setErr(ex.message); }
            }} style={inp}>
              <option value="">{t('pa_reg_stock_method_none')}</option>
              {(d?.methods || []).map((m: any) => <option key={m.key} value={m.key}>{m.th} · {m.en}</option>)}
            </select>
          )}
      </div>
      {(pv?.problems || []).map((p: string, i: number) => <div key={i} style={amber}>{p}</div>)}
      {pv?.rows?.length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 9 }}>
          <thead><tr>
            <th style={th}>{t('pa_reg_stock_th_item')}</th><th style={{ ...th, textAlign: 'right' }}>{t('pa_reg_stock_th_qty')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_reg_stock_th_cost')}</th><th style={{ ...th, textAlign: 'right' }}>{t('pa_reg_stock_th_market')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_reg_stock_th_carried')}</th><th style={{ ...th, textAlign: 'right' }}>{t('pa_reg_stock_th_writedown')}</th><th style={th} />
          </tr></thead>
          <tbody>
            {pv.rows.map((r: any) => (
              <tr key={r.itemId}>
                <td style={td}>{r.code} {r.nameTh}</td>
                <td style={num}>{r.quantity} {r.unit}</td>
                <td style={num}>{baht(r.costSatang)}</td>
                <td style={num}>
                  <input value={market[r.itemId] ?? ''} onChange={e => setMarket({ ...market, [r.itemId]: e.target.value })}
                    placeholder="—" style={{ ...inp, width: 100, textAlign: 'right' }} />
                </td>
                <td style={num}><b>{baht(r.carriedSatang)}</b></td>
                <td style={{ ...num, color: r.writedownSatang ? '#B42318' : 'var(--text-tertiary)' }}>{r.writedownSatang ? baht(r.writedownSatang) : '—'}</td>
                <td style={{ ...td, fontSize: 11, color: '#92400E' }}>{r.why}</td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr>
            <td style={{ ...td, fontWeight: 800 }}>{t('pa_reg_stock_total')}</td><td style={td} />
            <td style={{ ...num, fontWeight: 800 }}>{baht(pv.totalCostSatang)}</td><td style={td} />
            <td style={{ ...num, fontWeight: 800 }}>{baht(pv.totalCarriedSatang)}</td>
            <td style={{ ...num, fontWeight: 800 }}>{baht(pv.totalWritedownSatang)}</td><td style={td} />
          </tr></tfoot>
        </table>
      ) : <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pa_reg_stock_empty')}</div>}
      {pv?.ready && !closed && !readOnly && !(d?.struck || []).length && (
        <div style={{ ...row, marginTop: 9 }}>
          <button style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none' }} onClick={async () => {
            setErr(''); setMsg('');
            try {
              const r: any = await api.arStrikeValuation(clientId, periodId, asSatang());
              setMsg(r.writedownSatang ? t('pa_reg_stock_struck_msg', { amount: baht(r.writedownSatang), doc: r.docNo }) : t('pa_reg_stock_struck_none'));
              onChanged(); await load();
            } catch (e: any) { setErr(e.message); }
          }}><i className="ti ti-scale" aria-hidden /> {t('pa_reg_stock_strike_btn')}</button>
        </div>
      )}
      {(d?.struck || []).length ? <div style={{ fontSize: 12, color: '#166534', marginTop: 8 }}>
        {t('pa_reg_stock_struck_line', { n: (d.struck || []).length, date: dmy(d.struck[0].struck_on), name: d.struck[0].struck_by_name })}
      </div> : null}
      {msg && <div style={{ fontSize: 12.5, color: '#166534', background: '#E8F6EE', border: '1px solid #BBE5CB', padding: '8px 11px', borderRadius: 8, marginTop: 8 }}>{msg}</div>}
      <Err e={err} />
    </div>
  );
}

/** Accounting Professions Act s.11 — SLF's own registration, not a client's. */
function FirmRegistration() {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState<any[]>([]);
  const [companyId, setCompanyId] = useState('');
  const [d, setD] = useState<any>(null);
  const [f, setF] = useState<any>({ registrationNo: '', registeredOn: '', expiresOn: '', guaranteeAmount: '', guaranteeForm: '', guaranteePostedOn: '', offersAudit: false, signatoryName: '', signatoryCpaNo: '' });
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  useEffect(() => { api.arFirms().then((r: any) => { const cs = r.companies || []; setCompanies(cs); if (cs[0]) setCompanyId(cs[0].id); }).catch(() => {}); }, []);
  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const r: any = await api.arFirmRegistration(companyId);
      setD(r);
      setF(r.row ? {
        registrationNo: r.row.registrationNo || '', registeredOn: r.row.registeredOn || '', expiresOn: r.row.expiresOn || '',
        guaranteeAmount: r.row.guaranteeAmount ? String(r.row.guaranteeAmount / 100) : '', guaranteeForm: r.row.guaranteeForm || '',
        guaranteePostedOn: r.row.guaranteePostedOn || '', offersAudit: !!r.row.offersAudit,
        signatoryName: r.row.signatoryName || '', signatoryCpaNo: r.row.signatoryCpaNo || '',
      } : { registrationNo: '', registeredOn: '', expiresOn: '', guaranteeAmount: '', guaranteeForm: '', guaranteePostedOn: '', offersAudit: false, signatoryName: '', signatoryCpaNo: '' });
    } catch (e: any) { setErr(e.message); }
  }, [companyId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={rule}>
        {t('pa_reg_firm_rule')}
      </div>
      <div style={row}>
        <select value={companyId} onChange={e => setCompanyId(e.target.value)} style={{ ...inp, minWidth: 200 }}>
          {companies.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input placeholder={t('pa_reg_firm_regno_ph')} value={f.registrationNo} onChange={e => setF({ ...f, registrationNo: e.target.value })} style={{ ...inp, width: 170 }} />
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_reg_firm_registered')}<br />
          <input type="date" value={f.registeredOn} onChange={e => setF({ ...f, registeredOn: e.target.value })} style={{ ...inp, width: 140 }} /></label>
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_reg_firm_expires')}<br />
          <input type="date" value={f.expiresOn} onChange={e => setF({ ...f, expiresOn: e.target.value })} style={{ ...inp, width: 140 }} /></label>
      </div>
      <div style={row}>
        <input placeholder={t('pa_reg_firm_guarantee_ph')} value={f.guaranteeAmount} onChange={e => setF({ ...f, guaranteeAmount: e.target.value })} style={{ ...inp, width: 160 }} />
        <input placeholder={t('pa_reg_firm_guarantee_form_ph')} value={f.guaranteeForm} onChange={e => setF({ ...f, guaranteeForm: e.target.value })} style={{ ...inp, width: 170 }} />
        <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', gap: 5, alignItems: 'center' }}>
          <input type="checkbox" checked={f.offersAudit} onChange={e => setF({ ...f, offersAudit: e.target.checked })} /> {t('pa_reg_firm_offers_audit')}
        </label>
        {f.offersAudit && <>
          <input placeholder={t('pa_reg_firm_cpa_name_ph')} value={f.signatoryName} onChange={e => setF({ ...f, signatoryName: e.target.value })} style={{ ...inp, width: 150 }} />
          <input placeholder={t('pa_reg_firm_cpa_no_ph')} value={f.signatoryCpaNo} onChange={e => setF({ ...f, signatoryCpaNo: e.target.value })} style={{ ...inp, width: 140 }} />
        </>}
        <button style={btn} onClick={async () => {
          setErr(''); setMsg('');
          try {
            await api.arSaveFirmRegistration(companyId, { ...f, guaranteeAmount: toSatang(f.guaranteeAmount) });
            setMsg(t('pa_reg_saved')); await load();
          } catch (e: any) { setErr(e.message); }
        }}><i className="ti ti-device-floppy" aria-hidden /> {t('pa_reg_save')}</button>
        {msg && <span style={{ fontSize: 12, color: '#166534' }}>{msg}</span>}
      </div>
      {d?.row?.expired && <div style={red}>{t('pa_reg_firm_expired', { date: dmy(d.row.expiresOn) })}</div>}
      {d?.row?.expiringWithin60Days && <div style={amber}>{t('pa_reg_firm_expiring', { date: dmy(d.row.expiresOn) })}</div>}
      <div style={amber}>{d?.requirement?.note}</div>
      <Err e={err} />
    </div>
  );
}

export default function Registers({ clientId, periodId, periodClosed, onChanged }:
  { clientId: string; periodId: string; periodClosed: boolean; onChanged: () => void }) {
  const { t } = useTranslation();
  const readOnly = useReadOnlySeat();
  const [counts, setCounts] = useState<{ incidents: number; changes: number }>({ incidents: 0, changes: 0 });
  useEffect(() => {
    Promise.all([api.arIncidents(clientId).catch(() => null), api.arChanges(clientId).catch(() => null)])
      .then(([a, b]: any[]) => setCounts({ incidents: a?.overdue || 0, changes: b?.overdue || 0 }));
  }, [clientId]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
        {t('pa_reg_title')}
      </div>
      <Section title={t('pa_reg_sec_incidents_title')}
        subtitle={t('pa_reg_sec_incidents_sub')}
        badge={<Pill n={counts.incidents} tone="red" />}>
        <Incidents clientId={clientId} periodId={periodId} readOnly={readOnly} />
      </Section>
      <Section title={t('pa_reg_sec_cess_title')}
        subtitle={t('pa_reg_sec_cess_sub')}>
        <Cessation clientId={clientId} readOnly={readOnly} />
      </Section>
      <Section title={t('pa_reg_sec_insp_title')}
        subtitle={t('pa_reg_sec_insp_sub')}>
        <Inspections clientId={clientId} periodId={periodId} readOnly={readOnly} />
      </Section>
      <Section title={t('pa_reg_sec_chg_title')}
        subtitle={t('pa_reg_sec_chg_sub')}
        badge={<Pill n={counts.changes} tone="red" />}>
        <Changes clientId={clientId} readOnly={readOnly} />
      </Section>
      <Section title={`${t('pa_reg_doc_directors_report')} · ${t('pa_reg_sec_dr_title_gloss')}`}
        subtitle={t('pa_reg_sec_dr_sub')}>
        <DirectorsReport clientId={clientId} periodId={periodId} readOnly={readOnly} />
      </Section>
      <Section title={t('pa_reg_sec_stock_title')}
        subtitle={t('pa_reg_sec_stock_sub')}>
        <StockValuation clientId={clientId} periodId={periodId} closed={periodClosed} readOnly={readOnly} onChanged={onChanged} />
      </Section>
      <Section title={t('pa_reg_sec_vat_title')}
        subtitle={t('pa_reg_sec_vat_sub')}>
        <VatThreshold clientId={clientId} />
      </Section>
      {/* The FIRM's own TFAC registration (every company in the installation), not this client's: both its
          routes are in middleware.ts BOOKS_DENY, so even opening the fold would be a refused read. */}
      {!readOnly && (
        <Section title={`${t('pa_reg_doc_firm_register')} · ${t('pa_reg_sec_firm_title_gloss')}`}
          subtitle={t('pa_reg_sec_firm_sub')}>
          <FirmRegistration />
        </Section>
      )}
    </div>
  );
}
